// src/components/NewJobView/Tabs/ProductDetailsTab.js

import React, { useState } from 'react';
import { PlusCircle, Trash2, Image as ImageIcon } from 'lucide-react';

// --- Componente para la Carga de Previews de Piezas (integrado) ---
const PiecePreviewUploader = ({ image, onImageSet }) => {
    const handlePaste = (e) => { 
        const items = e.clipboardData.items; 
        for (let i = 0; i < items.length; i++) { 
            if (items[i].type.indexOf('image') !== -1) { 
                const blob = items[i].getAsFile(); 
                const reader = new FileReader(); 
                reader.onload = (event) => onImageSet(event.target.result); 
                reader.readAsDataURL(blob); 
            }
        }
    };
    const handleDrop = (e) => { 
        e.preventDefault(); 
        const file = e.dataTransfer.files[0]; 
        if (file && file.type.startsWith('image/')) { 
            const reader = new FileReader(); 
            reader.onload = (event) => onImageSet(event.target.result); 
            reader.readAsDataURL(file); 
        }
    };
    const handleDragOver = (e) => e.preventDefault();

    return (
        <div 
            onDrop={handleDrop} 
            onDragOver={handleDragOver} 
            onPaste={handlePaste} 
            className="flex justify-center items-center border-2 border-gray-300 border-dashed rounded-md w-full h-24 relative bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer p-1"
        >
            {image ? 
                <img src={image} alt="Preview de Pieza" className="max-h-full max-w-full object-contain"/> : 
                <div className="text-center">
                    <ImageIcon className="mx-auto h-8 w-8 text-gray-400" />
                    <p className="text-xs text-gray-500 mt-1">Pegar o arrastrar</p>
                </div>
            }
        </div>
    );
};


const ProductDetailsTab = ({
  jobData,
  handleInputChange,
  products,
  handleDynamicChange,
  addDynamicField,
  removeDynamicField,
  handleBlockCodeBlur,
  setJobData,
  allPiecesMasterList
}) => {
  const [autocomplete, setAutocomplete] = useState({ suggestions: [], activeIndex: -1 });

  const handlePieceChange = (index, field, value) => {
    const updatedComponents = jobData.components.map((item, i) => i === index ? { ...item, [field]: value } : item);
    setJobData(prev => ({ ...prev, components: updatedComponents }));
    
    if (field === 'name' || field === 'code') {
        const searchTerm = value.toLowerCase();
        if (searchTerm.length > 1) {
            const suggestions = allPiecesMasterList.filter(p => p[field] && p[field].toLowerCase().includes(searchTerm));
            setAutocomplete({ suggestions, activeIndex: index });
        } else {
            setAutocomplete({ suggestions: [], activeIndex: -1 });
        }
    }
  };
  
  const handleSelectSuggestion = (pieceIndex, suggestion) => {
    const updatedComponents = jobData.components.map((comp, index) => {
        if (index === pieceIndex) {
            return {
                ...comp,
                name: suggestion.name,
                code: suggestion.code,
                image: { ...comp.image, preview: suggestion.previewUrl || comp.image.preview }
            };
        }
        return comp;
    });
    setJobData(prev => ({ ...prev, components: updatedComponents }));
    setAutocomplete({ suggestions: [], activeIndex: -1 });
  };

  const renderSubType = () => {
    switch (jobData.type) {
      case 'Producto':
        return (
          <div>
            <label htmlFor="product" className="text-sm font-bold text-gray-600 block mb-2">Producto</label>
            <select name="product" id="product" value={jobData.product} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md">
              <option value="" disabled>-- Selecciona --</option>
              {(products || []).map(prod => <option key={prod} value={prod}>{prod}</option>)}
            </select>
          </div>
        );
      case 'Servicio':
      case 'Proyecto':
        return (
          <div>
            <label htmlFor="class" className="text-sm font-bold text-gray-600 block mb-2">Clase</label>
            <select name="class" id="class" value={jobData.class} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md">
                <option value="" disabled>-- Selecciona --</option>
                {jobData.type === 'Servicio' ? <>
                    <option value="Impresion">Impresion</option>
                    <option value="Corte">Corte</option>
                    <option value="Router">Router</option>
                    <option value="Laser">Laser</option>
                </> : <>
                    <option value="Booth">Booth</option>
                    <option value="Evento">Evento</option>
                </>}
            </select>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      {/* --- SECCIÓN TIPO/PRODUCTO --- */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div>
          <label htmlFor="type" className="text-sm font-bold text-gray-600 block mb-2">Tipo</label>
          <select name="type" id="type" value={jobData.type} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md">
            <option value="Producto">Producto</option>
            <option value="Servicio">Servicio</option>
            <option value="Proyecto">Proyecto</option>
          </select>
        </div>
        {renderSubType()}
        <div>
          <label htmlFor="quantity" className="text-sm font-bold text-gray-600 block mb-2">Cantidad</label>
          <input type="number" name="quantity" id="quantity" value={jobData.quantity} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md" min="1" />
        </div>
      </div>
      
      {/* --- SECCIÓN DE COMPONENTES --- */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Componentes</h3>
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-3 py-2 text-center w-40">Preview</th>
                        <th className="px-3 py-2 text-left">Nombre</th>
                        <th className="px-3 py-2 text-left">Código <span className="text-red-500">*</span></th>
                        <th className="px-3 py-2 text-center">Cant. por Producto</th>
                        <th className="px-3 py-2 text-center">Total Piezas</th>
                        <th className="px-3 py-2 text-center">Acciones</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {(jobData.components || []).map((comp, index) => (
                        <tr key={index}>
                            <td className="px-3 py-2 align-middle text-center">
                                <PiecePreviewUploader 
                                    image={comp.image.preview} 
                                    onImageSet={(imgData) => {
                                        const updatedComponents = [...jobData.components];
                                        updatedComponents[index].image.preview = imgData;
                                        setJobData(prev => ({...prev, components: updatedComponents}));
                                    }}
                                />
                            </td>
                            <td className="px-3 py-2 align-middle relative">
                                <input type="text" value={comp.name || ''} onChange={(e) => handlePieceChange(index, 'name', e.target.value)} className="w-full p-1 border border-gray-300 rounded"/>
                                {autocomplete.activeIndex === index && autocomplete.suggestions.length > 0 && (
                                    <div className="absolute z-10 w-full bg-white border mt-1 rounded-md shadow-lg max-h-40 overflow-y-auto">
                                        {autocomplete.suggestions.map(s => (<div key={s.code} onClick={() => handleSelectSuggestion(index, s)} className="p-2 hover:bg-gray-100 cursor-pointer">{s.name} ({s.code})</div>))}
                                    </div>
                                )}
                            </td>
                            <td className="px-3 py-2 align-middle relative">
                                <input type="text" placeholder="Código *" value={comp.code || ''} onChange={(e) => handlePieceChange(index, 'code', e.target.value)} className={`w-full p-1 border rounded ${(comp.name || '').trim() && !(comp.code || '').trim() ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`}/>
                                {autocomplete.activeIndex === index && autocomplete.suggestions.length > 0 && (
                                    <div className="absolute z-10 w-full bg-white border mt-1 rounded-md shadow-lg max-h-40 overflow-y-auto">
                                        {autocomplete.suggestions.map(s => (<div key={s.code} onClick={() => handleSelectSuggestion(index, s)} className="p-2 hover:bg-gray-100 cursor-pointer">{s.code} ({s.name})</div>))}
                                    </div>
                                )}
                            </td>
                            <td className="px-3 py-2 align-middle text-center">
                                <input type="number" value={comp.qtyPerProduct} onChange={(e) => handlePieceChange(index, 'qtyPerProduct', e.target.value)} className="w-20 p-1 border border-gray-300 rounded text-center"/>
                            </td>
                            <td className="px-3 py-2 font-medium align-middle text-center">
                                {parseInt(comp.qtyPerProduct || 0) * parseInt(jobData.quantity || 0)}
                            </td>
                            <td className="px-3 py-2 align-middle text-center">
                                <button type="button" onClick={() => removeDynamicField(index, 'components')} className="text-red-500 hover:text-red-700 mx-auto">
                                    <Trash2 size={16}/>
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        <div className="flex items-center gap-2 mt-4">
            <button type="button" onClick={() => addDynamicField('components')} className="flex items-center gap-2">
                <PlusCircle size={20} className="text-green-500 hover:text-green-600" />
                <span className="text-sm text-green-600">Añadir Componente</span>
            </button>
        </div>
      </div>

      {/* --- SECCIÓN DE BLOQUES --- */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center mb-4">
          <input type="checkbox" id="hasBlocks" name="hasBlocks" checked={jobData.hasBlocks} onChange={handleInputChange} className="h-4 w-4 rounded" />
          <label htmlFor="hasBlocks" className="ml-2 text-lg font-semibold text-gray-700">Bloques</label>
        </div>
        {jobData.hasBlocks && (
          <div className="space-y-4">
            {jobData.blocks.map((block, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-[1fr,auto,auto] gap-4 items-center p-2 border rounded-md">
                <input type="text" name="name" placeholder="Nombre del bloque" value={block.name} onChange={(e) => handleDynamicChange(index, e, 'blocks')} className="w-full p-2 border rounded-md" />
                <input type="text" name="code" placeholder="Código (3)" value={block.code} maxLength="3" onChange={(e) => handleDynamicChange(index, e, 'blocks')} onBlur={() => handleBlockCodeBlur(index)} className="w-24 p-2 border rounded-md text-center" />
                <button type="button" onClick={() => removeDynamicField(index, 'blocks')}>
                    <Trash2 size={20} className="text-red-500 hover:text-red-600" />
                </button>
              </div>
            ))}
            <div className="flex items-center gap-2 mt-4">
                <button type="button" onClick={() => addDynamicField('blocks')} className="flex items-center gap-2">
                    <PlusCircle size={20} className="text-green-500 hover:text-green-600" />
                    <span className="text-sm text-green-600">Añadir Bloque</span>
                </button>
            </div>
          </div>
        )}
      </div>

      {/* The sections for Members and Roles have been removed from this file. */}
      {/* They are now handled by the dedicated TeamAndRolesTab.js component. */}

    </div>
  );
};

export default ProductDetailsTab;

